import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';

import {
  getDashboardPanelTokens,
  rem,
  TILE_TITLE_FONT_SIZE,
  TILE_TITLE_FONT_WEIGHT,
} from '../dashboardPanelTokens';
import { CARD_PADDING_REM } from '../../../../components/panels/panelLayout';
import type { CoverageGroup, CoverageLine } from './coverage.types';
import { describeGroup } from './coverageNarrative';
import { heroFacts, premiumDelta } from './coverageFacts';
import { date, money } from './coverageFormat';
import { getToneTokens } from './coverageTokens';
import { coverageHref, policyHref, quoteHref } from './coverageLinks';
import {
  CoverageEyebrow,
  CoverageFacts,
  CoverageStatusPill,
  CoverageTermBar,
} from './CoverageParts';
import { TilePrimaryButton, TileSecondaryButton } from './CoverageActions';
import { CoverageBand } from './CoverageBand';
import {
  hasDownloadableDocument,
  useCoverageDownload,
} from './useCoverageDownload';

interface CoverageTileProps {
  readonly group: CoverageGroup;
  readonly insuredName: string;
  readonly now?: string | number | Date;
}

/**
 * The account's headline coverage, chosen by `buildCoverageView` rather than by
 * whichever line the API returned first.
 *
 * The subject is a single line -- the policy in force, or the quote when there
 * is no policy. Everything else in the group attaches underneath as a band, so
 * a renewal quote or an already-bound next term reads as part of this coverage
 * rather than as separate cover the reader has to reconcile.
 */
export function CoverageTile({
  group,
  insuredName,
  now = Date.now(),
}: Readonly<CoverageTileProps>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);
  const caution = getToneTokens('caution', palette.mode);

  const { download, isDownloading } = useCoverageDownload();
  const subject = getSubject(group);

  if (!subject) {
    return null;
  }

  const isQuote = subject.kind === 'quote';
  const narrative = describeGroup(group, now);
  const caption = limitCaption(subject);

  return (
    <Box
      data-qa="COVERAGE_TILE"
      sx={{
        minWidth: 0,
        // A column, so the closing block below can be pushed to the bottom edge
        // when the row beside it is taller than this tile's content.
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        // A quote is not coverage. The dashed edge and the tint are the tile
        // saying so before a word is read.
        //
        // `width: auto` under the negative margins, not `100%`: a percentage
        // width resolves against the container from the shifted left edge, so
        // the frame reached the card's left edge and stopped short of its
        // right by exactly the padding it was cancelling.
        //
        // The margins put this border on the card's own edges, so its corners
        // are the card's corners and its radius has to be the card's radius.
        // Written independently it was 12 against the card's 5, and the two
        // curves sat visibly apart at every corner.
        //
        // `--tile-radius`, not a local rem: the card reads the same property,
        // so the two cannot be given different curves. A rem constant matched
        // only at the 12px root -- past 1620px the root re-bases and the frame
        // grew to 5.83 and then 6.67 against a card still painting MUI's
        // fixed 5.
        ...(isQuote
          ? {
              width: 'auto',
              margin: `-${CARD_PADDING_REM}`,
              padding: CARD_PADDING_REM,
              border: `${rem(2)} dashed ${caution.ink}66`,
              borderRadius: 'var(--tile-radius)',
              backgroundColor: caution.surface,
            }
          : { width: '100%' }),
      }}
    >
      <Box display="flex" flexWrap="wrap" alignItems="flex-start" gap={rem(16)}>
        <Box sx={{ flex: '1 1 auto', minWidth: rem(240) }}>
          <CoverageEyebrow kind={subject.kind} />
          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            gap={rem(10)}
            sx={{ marginTop: rem(4) }}
          >
            <Typography
              component="h3"
              sx={{
                margin: 0,
                fontSize: TILE_TITLE_FONT_SIZE,
                fontWeight: TILE_TITLE_FONT_WEIGHT,
                lineHeight: 1.25,
                letterSpacing: '-0.015em',
                color: tokens.ink,
              }}
            >
              {group.productLabel}
            </Typography>
            <CoverageStatusPill status={subject.status} />
          </Box>
          <Typography
            sx={{
              marginTop: rem(2),
              fontSize: rem(13),
              color: tokens.inkFaint,
            }}
          >
            {isQuote ? 'Quote' : 'Policy'} {subject.reference || '—'} ·{' '}
            {insuredName}
          </Typography>
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          gap={rem(8)}
          sx={{ marginLeft: 'auto' }}
        >
          {/*
            Only where the document exists. A bound policy has no certificate
            yet; the button offered one anyway and answered 404 (FIR-21145).
          */}
          {hasDownloadableDocument(subject) && (
            <TileSecondaryButton
              onClick={() => download(subject)}
              disabled={isDownloading}
              startIcon={<FontAwesomeIcon icon={faDownload} />}
            >
              {isQuote ? 'Download quote' : 'Certificate of insurance'}
            </TileSecondaryButton>
          )}
          <TilePrimaryButton component={Link} to={coverageHref(subject)}>
            {isQuote ? 'Review & bind' : 'View policy'}
          </TilePrimaryButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: rem(24),
          marginTop: rem(16),
          marginBottom: rem(16),
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1fr) minmax(0, 1.6fr)',
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: rem(11),
              fontWeight: 500,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: tokens.inkFaint,
            }}
          >
            {isQuote ? 'Proposed aggregate limit' : 'Aggregate limit'}
          </Typography>
          <Typography
            sx={{
              marginTop: rem(2),
              fontSize: rem(38),
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '-0.025em',
              fontVariantNumeric: 'tabular-nums',
              color: isQuote ? tokens.inkBody : tokens.ink,
            }}
          >
            {money(subject.aggregateLimit)}
          </Typography>
          {caption && (
            <Typography
              sx={{
                marginTop: rem(6),
                fontSize: rem(13),
                color: tokens.inkFaint,
              }}
            >
              {caption}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            minWidth: 0,
            paddingLeft: { xs: 0, md: rem(24) },
            borderLeft: { xs: 'none', md: `1px solid ${tokens.line}` },
          }}
        >
          <CoverageFacts facts={heroFacts(subject, now)} />
        </Box>
      </Box>

      {/*
        The closing block takes the row's slack. `auto`, not a floor on the
        tile: a floor is not a definite height, so nothing inside resolves
        against it and the leftover collects as dead space under the last line
        instead. Here it opens above the rule, and resolves to 0 -- leaving the
        16 on the block above to hold the two apart -- when there is no slack.
      */}
      <Box
        sx={{
          marginTop: 'auto',
          paddingTop: rem(12),
          borderTop: `1px solid ${tokens.line}`,
        }}
      >
        <Typography
          sx={{
            fontSize: rem(13),
            fontWeight: 700,
            lineHeight: 1.4,
            color: tokens.ink,
          }}
        >
          {narrative.headline}
        </Typography>
        {/*
          Only where a term is actually running. An empty track under expired
          or cancelled coverage reads as "none of it elapsed", which is the
          opposite of what happened.
        */}
        {narrative.progress !== undefined && (
          <CoverageTermBar
            progress={narrative.progress}
            active={narrative.tone !== 'neutral'}
            label={group.productLabel}
          />
        )}
        {narrative.detail && (
          <Typography
            sx={{
              marginTop: rem(8),
              fontSize: rem(12),
              lineHeight: 1.5,
              color: tokens.inkFaint,
            }}
          >
            {narrative.detail}
          </Typography>
        )}
      </Box>

      <CoverageTileBands group={group} subject={subject} now={now} />
    </Box>
  );
}

/**
 * The limit is stated in the tense the coverage is actually in. Expired cover
 * still had a limit, and saying "any one claim" of a policy that ended is a
 * promise it no longer makes.
 */
function limitCaption(line: CoverageLine): string | undefined {
  if (line.kind === 'quote') {
    return 'Nothing is covered under this quote until you bind it.';
  }

  // Deliberately silent on how the limit applies. It used to read "any one claim
  // and in the aggregate", but the basis varies by policy -- on an Any Claim
  // policy every claim gets its full limit and its full deductible, so that
  // wording is wrong there. Nothing in the read distinguishes them.
  if (line.status === 'expired' || line.status === 'cancelled') {
    return 'The limit that applied while this policy was in force.';
  }

  return undefined;
}

/** The line the tile is about: coverage first, an offer only when there is none. */
function getSubject(group: CoverageGroup): CoverageLine | undefined {
  if (group.current) {
    return group.current;
  }

  if (group.lapsed) {
    return group.lapsed;
  }

  return group.quotes[0];
}

interface CoverageTileBandsProps {
  readonly group: CoverageGroup;
  readonly subject: CoverageLine;
  readonly now: string | number | Date;
}

function CoverageTileBands({
  group,
  subject,
  now,
}: Readonly<CoverageTileBandsProps>) {
  const pending = group.quotes.filter(
    (quote) => quote.status === 'quoteReady' || quote.status === 'quoteBlocked'
  );
  const options = pending.filter((quote) => quote.relation === 'option');

  if (group.upcoming) {
    return (
      <CoverageBand
        kind="policy"
        headline={`Next term already bound · ${group.upcoming.reference}`}
        detail={`Coverage continues without a gap from ${date(
          group.upcoming.effectiveDate
        )} through ${date(group.upcoming.termEnd)}.`}
        actionLabel="View next term"
        actionHref={policyHref(group.upcoming)}
      />
    );
  }

  // An option set is one decision, so it gets one band rather than one per
  // quote -- otherwise the reader is asked to make the same choice N times.
  if (options.length > 1) {
    return (
      <CoverageBand
        kind="quote"
        headline={`${options.length} quote options ready · from ${money(
          Math.min(
            ...options.map((quote) => quote.premium ?? Number.MAX_SAFE_INTEGER)
          )
        )}`}
        detail="Different limits and retentions for the same coverage. Only one can be bound."
        actionLabel="Compare options"
        actionHref={quoteHref(options[0])}
      />
    );
  }

  // A quote attached to coverage that is already the subject: the renewal, or
  // an offer alongside an in-force policy.
  const attached = pending.find((quote) => quote.id !== subject.id);

  if (!attached) {
    return null;
  }

  const delta = premiumDelta(group.current, attached);

  return (
    <CoverageBand
      kind="quote"
      headline={`${
        attached.isRenewal ? 'Renewal quote' : 'Quote'
      } ready · ${money(attached.premium)}`}
      detail={`${delta ? `${delta.phrase}. ` : ''}Quote ${
        attached.reference
      } expires ${date(attached.expiresOn)}.`}
      actionLabel={attached.isRenewal ? 'Renew' : 'Review & bind'}
      actionHref={quoteHref(attached)}
    />
  );
}
